import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Award,
  Briefcase,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  ShieldAlert,
  Sparkles,
  Trash2,
  User,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import apiClient from "@/lib/api";
import { useNeeds } from "@/hooks/useStaffing";
import { updateCandidatePoolStatus, createApplication } from "@/lib/recruitmentApi";
import ConfirmationModal from "@/components/ui/ConfirmationModal";

export default function TalentPoolPage() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const { needs } = useNeeds();

  // Delegation modal state
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [selectedCandidateForNeed, setSelectedCandidateForNeed] = useState(null);
  const [selectedNeedId, setSelectedNeedId] = useState("");
  const [linkingSuccess, setLinkingSuccess] = useState(null);

  // Candidate removal modal state
  const [candidateToRemove, setCandidateToRemove] = useState(null);
  const [removingFromPool, setRemovingFromPool] = useState(false);

  // Autocomplete candidate search inside admission modal
  const [candidateSearchQuery, setCandidateSearchQuery] = useState("");
  const [candidateSearchResults, setCandidateSearchResults] = useState([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [selectedCandidateForAdmission, setSelectedCandidateForAdmission] = useState(null);

  const [admitForm, setAdmitForm] = useState({
    candidate_id: "",
    director_name: "",
    rh_name: "",
    reason: "",
  });
  const [admitting, setAdmitting] = useState(false);
  const [admitError, setAdmitError] = useState(null);

  const fetchPoolCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/recruitment/candidates", {
        params: { pool_status: "in_pool" },
      });
      setCandidates(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec du chargement du vivier de talents.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPoolCandidates();
  }, [fetchPoolCandidates]);

  const filteredCandidates = useMemo(() => {
    return candidates.filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const nameMatch = `${c.first_name} ${c.last_name}`.toLowerCase().includes(q);
      const emailMatch = (c.email || "").toLowerCase().includes(q);
      const roleMatch = (c.title || c.current_role || "").toLowerCase().includes(q);
      const skillsMatch = (c.skills || c.technologies || []).some((s) =>
        String(s).toLowerCase().includes(q)
      );
      return nameMatch || emailMatch || roleMatch || skillsMatch;
    });
  }, [candidates, search]);

  // Live search for candidate autocomplete in admission modal
  useEffect(() => {
    if (!showAdmitModal || !candidateSearchQuery.trim()) {
      setCandidateSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingCandidates(true);
      try {
        const res = await apiClient.get("/recruitment/candidates", {
          params: { search: candidateSearchQuery.trim() },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        // Filter out candidates already in pool
        setCandidateSearchResults(list.filter((c) => c.pool_status !== "in_pool" && c.pool?.status !== "in_pool"));
      } catch {
        setCandidateSearchResults([]);
      } finally {
        setSearchingCandidates(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [showAdmitModal, candidateSearchQuery]);

  const confirmRemoveFromPool = async (reason) => {
    if (!candidateToRemove) return;
    setRemovingFromPool(true);
    try {
      await updateCandidatePoolStatus(candidateToRemove._id, "removed", reason || "Retrait manuel par RH");
      toast.success(`${candidateToRemove.first_name} ${candidateToRemove.last_name} retiré du vivier.`);
      setCandidateToRemove(null);
      await fetchPoolCandidates();
    } catch (err) {
      toast.error("Échec du retrait : " + (err?.response?.data?.detail || err.message));
    } finally {
      setRemovingFromPool(false);
    }
  };

  const handleLinkToNeed = async (e) => {
    e.preventDefault();
    if (!selectedCandidateForNeed || !selectedNeedId) return;
    try {
      await createApplication({
        candidate_id: selectedCandidateForNeed._id,
        need_id: selectedNeedId,
      });
      toast.success(`${selectedCandidateForNeed.first_name} positionné avec succès sur la mission.`);
      setLinkingSuccess(`Candidat positionné avec succès sur la mission.`);
      setSelectedCandidateForNeed(null);
      setSelectedNeedId("");
      setTimeout(() => setLinkingSuccess(null), 4000);
    } catch (err) {
      toast.error("Échec du positionnement : " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleAdmitWithDelegation = async (e) => {
    e.preventDefault();
    if (!admitForm.candidate_id || !admitForm.director_name.trim() || !admitForm.reason.trim()) {
      setAdmitError("Veuillez renseigner tous les champs obligatoires (directeur, motif).");
      return;
    }
    setAdmitting(true);
    setAdmitError(null);
    try {
      const fullReason = `Accord Directeur : ${admitForm.director_name} | Validé par RH : ${admitForm.rh_name || "RH"} | Motif : ${admitForm.reason}`;
      await updateCandidatePoolStatus(admitForm.candidate_id, "in_pool", fullReason);
      setShowAdmitModal(false);
      setAdmitForm({ candidate_id: "", director_name: "", rh_name: "", reason: "" });
      await fetchPoolCandidates();
    } catch (err) {
      setAdmitError(err?.response?.data?.detail || "Échec de l'admission au vivier.");
    } finally {
      setAdmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-16" data-testid="talent-pool-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-0.5 text-xs font-semibold text-amber-300 border border-amber-500/20 mb-2">
            <Award className="h-3.5 w-3.5" />
            Parcours Indépendant sans Besoin Client
          </div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
            Vivier de Talents LENVIA
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Talents qualifiés et validés, disponibles pour de futures opportunités de mission.
          </p>
        </div>

        <button
          onClick={() => setShowAdmitModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-amber-500 transition-all self-start sm:self-auto"
          data-testid="admit-talent-pool-button"
        >
          <UserPlus className="h-4 w-4" />
          Inscrire au vivier (RH / Directeur)
        </button>
      </div>

      {linkingSuccess && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>{linkingSuccess}</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans le vivier (nom, rôle, technologie)..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
            data-testid="talent-pool-search"
          />
        </div>
        <span className="text-xs text-slate-400 font-medium">
          {filteredCandidates.length} profil(s) qualifié(s) dans le vivier
        </span>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-rose-300 text-xs">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredCandidates.length === 0 && (
        <div className="text-center py-16 glass rounded-2xl border border-white/10 text-slate-500">
          <Award className="h-10 w-10 mx-auto text-amber-500/40 mb-3" />
          <p className="text-base font-semibold text-slate-300">Aucun candidat dans le vivier</p>
          <p className="text-xs text-slate-500 mt-1">
            Les candidats qualifiés RH ou validés par un directeur peuvent être inscrits ici.
          </p>
        </div>
      )}

      {/* Pool Candidates Cards */}
      {!loading && filteredCandidates.length > 0 && (
        <div className="grid gap-3.5">
          {filteredCandidates.map((cand) => {
            const skills = cand.skills || cand.technologies || [];
            const poolData = cand.pool || {};

            return (
              <div
                key={cand._id}
                className="glass rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                data-testid={`talent-pool-card-${cand._id}`}
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="h-11 w-11 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-300 font-bold text-base shrink-0">
                    <Award className="h-5 w-5" />
                  </div>

                  <div className="space-y-1.5 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/admin/recrutement/candidats/${cand._id}`}
                        className="font-bold text-white text-base hover:text-amber-300 transition-colors"
                      >
                        {cand.first_name} {cand.last_name}
                      </Link>
                      {cand.title && (
                        <span className="text-xs text-slate-300 font-medium truncate">
                          • {cand.title}
                        </span>
                      )}
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        Inscrit au vivier
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5 text-slate-500" />
                        {cand.email}
                      </span>
                      {cand.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5 text-slate-500" />
                          {cand.phone}
                        </span>
                      )}
                      {cand.availability && (
                        <span className="text-slate-500">
                          Disponibilité : <span className="text-slate-200">{cand.availability}</span>
                        </span>
                      )}
                    </div>

                    {/* Delegation history details */}
                    {poolData.reason && (
                      <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5 text-[11px] text-slate-400 max-w-2xl">
                        <span className="text-slate-500 font-semibold">Traçabilité admission : </span>
                        {poolData.reason}
                      </div>
                    )}

                    {/* Skills pills */}
                    {skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {skills.slice(0, 6).map((s, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700/60"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                  <button
                    type="button"
                    onClick={() => setSelectedCandidateForNeed(cand)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-xl text-xs font-semibold transition-colors"
                    data-testid={`position-need-btn-${cand._id}`}
                  >
                    <Briefcase className="h-3.5 w-3.5" />
                    Positionner sur besoin
                  </button>

                  <Link
                    to={`/admin/recrutement/candidats/${cand._id}`}
                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-xl text-xs font-semibold transition-colors"
                  >
                    Dossier
                  </Link>

                  <button
                    type="button"
                    onClick={() => setCandidateToRemove(cand)}
                    className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-colors"
                    title="Retirer du vivier"
                    data-testid={`remove-pool-btn-${cand._id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal for Removing from Pool (replacing window.prompt) */}
      <ConfirmationModal
        isOpen={Boolean(candidateToRemove)}
        onClose={() => setCandidateToRemove(null)}
        title="Retirer du vivier de talents"
        message={`Confirmez-vous le retrait de ${candidateToRemove?.first_name} ${candidateToRemove?.last_name} du vivier ?`}
        confirmText="Confirmer le retrait"
        variant="warning"
        requireReason={true}
        reasonLabel="Motif du retrait du vivier"
        reasonPlaceholder="ex: Embauché en CDI chez un client, indisponible, réorientation..."
        isSubmitting={removingFromPool}
        onConfirm={confirmRemoveFromPool}
        testId="remove-pool-modal"
      />

      {/* Position on Need Modal */}
      {selectedCandidateForNeed && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-400" />
              Positionner sur un besoin client
            </h2>
            <p className="text-xs text-slate-400">
              Positionner <span className="text-white font-semibold">{selectedCandidateForNeed.first_name} {selectedCandidateForNeed.last_name}</span> sur une mission client active.
            </p>

            <form onSubmit={handleLinkToNeed} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Sélectionner le besoin *</label>
                <select
                  value={selectedNeedId}
                  onChange={(e) => setSelectedNeedId(e.target.value)}
                  className="w-full rounded-lg bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="">-- Choisir une mission --</option>
                  {needs.filter((n) => n.status !== "cancelled").map((n) => (
                    <option key={n._id} value={n._id}>
                      {n.title} {n.client_id ? `(${n.client_id})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setSelectedCandidateForNeed(null)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!selectedNeedId}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md"
                >
                  Confirmer le positionnement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admission with Delegation Modal - Enhanced with Autocomplete & Identity Check */}
      {showAdmitModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4 my-8">
            <div className="flex items-start justify-between pb-2 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">
                    Admission Vivier avec Accord Direction
                  </h2>
                  <p className="text-xs text-slate-400">
                    Recherche directe du candidat sans saisie manuelle d&apos;identifiant technique.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAdmitModal(false);
                  setSelectedCandidateForAdmission(null);
                  setCandidateSearchQuery("");
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {admitError && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                {admitError}
              </div>
            )}

            <form onSubmit={handleAdmitWithDelegation} className="space-y-4">
              {/* Autocomplete Candidate Search */}
              {!selectedCandidateForAdmission ? (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-300">
                    Rechercher le candidat par nom ou compétence <span className="text-amber-400">*</span>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={candidateSearchQuery}
                      onChange={(e) => {
                        setCandidateSearchQuery(e.target.value);
                        setAdmitForm((prev) => ({ ...prev, candidate_id: e.target.value }));
                      }}
                      placeholder="ex: Alexandre Dupont, DevOps, React..."
                      className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                      data-testid="admit-candidate-id-input"
                    />
                  </div>

                  {searchingCandidates && (
                    <div className="py-4 flex justify-center text-slate-400">
                      <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                    </div>
                  )}

                  {!searchingCandidates && candidateSearchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1.5 p-1 bg-slate-900/80 rounded-xl border border-white/10">
                      {candidateSearchResults.map((c) => (
                        <div
                          key={c._id}
                          onClick={() => {
                            setSelectedCandidateForAdmission(c);
                            setAdmitForm((prev) => ({ ...prev, candidate_id: c._id }));
                          }}
                          className="p-2.5 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 cursor-pointer text-xs transition-colors flex items-center justify-between"
                          data-testid={`select-candidate-${c._id}`}
                        >
                          <div>
                            <p className="font-semibold text-white">{c.first_name} {c.last_name}</p>
                            <p className="text-[11px] text-slate-400">{c.email} • {c.title || "Profil"}</p>
                          </div>
                          <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium">
                            Sélectionner
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {!searchingCandidates && candidateSearchQuery.trim() && candidateSearchResults.length === 0 && (
                    <p className="text-[11px] text-slate-400 text-center py-2">
                      Aucun candidat correspondant. Vous pouvez également saisir un identifiant direct.
                    </p>
                  )}
                </div>
              ) : (
                /* Selected Candidate Identity Card */
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-emerald-400" />
                        <span className="text-xs font-bold text-white">
                          {selectedCandidateForAdmission.first_name} {selectedCandidateForAdmission.last_name}
                        </span>
                        <span className="px-2 py-0.2 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-300">
                          Identité vérifiée
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-300 mt-1">
                        {selectedCandidateForAdmission.email}
                        {selectedCandidateForAdmission.phone ? ` • ${selectedCandidateForAdmission.phone}` : ""}
                        {selectedCandidateForAdmission.title ? ` • ${selectedCandidateForAdmission.title}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCandidateForAdmission(null);
                        setAdmitForm((prev) => ({ ...prev, candidate_id: "" }));
                      }}
                      className="text-xs text-amber-300 hover:text-white underline font-medium"
                    >
                      Changer
                    </button>
                  </div>

                  <div className="pt-1 flex items-center gap-2">
                    <Link
                      to={`/admin/recrutement/candidats/${selectedCandidateForAdmission._id}`}
                      target="_blank"
                      className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                    >
                      <span>Consulter la fiche complète</span>
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>

                  <input
                    type="hidden"
                    value={admitForm.candidate_id}
                    data-testid="admit-candidate-id-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Directeur ayant donné son accord *</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Marc Dupont (CTO / Dir. Associé)"
                    value={admitForm.director_name}
                    onChange={(e) => setAdmitForm({ ...admitForm, director_name: e.target.value })}
                    className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                    data-testid="admit-director-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Collaborateur RH réalisant la saisie *</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Julie Martin (RH Senior)"
                    value={admitForm.rh_name}
                    onChange={(e) => setAdmitForm({ ...admitForm, rh_name: e.target.value })}
                    className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                    data-testid="admit-rh-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Motif ou contexte de la décision *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="ex: Profil rare en IA générative rencontré au salon tech, potentiel pour les missions Q4, accord oral du CTO..."
                  value={admitForm.reason}
                  onChange={(e) => setAdmitForm({ ...admitForm, reason: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none"
                  data-testid="admit-reason-input"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowAdmitModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={admitting || !admitForm.candidate_id}
                  className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md flex items-center gap-1.5"
                  data-testid="admit-submit-btn"
                >
                  {admitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Confirmer l&apos;inscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
